import {
  Box,
  MenuItem,
  Select,
  SxProps,
  Typography,
  useTheme
} from '@mui/material';
import { Button } from 'system/atoms';
import CheckIcon from '@mui/icons-material/Check';
import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { organizationGroupOrganizationsAtom } from 'services/organizationGroupOrganizations/atom';
import { KeyboardArrowDown, OpenInNew } from '@mui/icons-material';
import { Paths } from 'services/navigation/paths';
import { OrganizationGroupRole } from 'generated-graphql/types';
import { useAuth } from 'services/auth/hooks';
import { useUser } from 'core/stores/hooks/useUser';
import { useUserFetcher } from 'services/user/hooks';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

type Option = {
  value: string;
  label: string;
};

const selectStyling: SxProps = {
  border: '1px solid white',
  borderRadius: '8px',
  color: 'white',
  '.MuiOutlinedInput-notchedOutline': { border: 0 },
  minWidth: 280,
  '& .MuiSelect-select': {
    py: '14px',
    px: '14px',
    border: 0
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  }
};

const menuItemStyling: SxProps = {
  px: 2,
  py: 1
};

const buttonStyling: SxProps = {
  display: 'flex',
  justifyContent: 'flex-start',
  px: 0,
  py: 0
};

const administratorMenuItem: SxProps = {
  justifyContent: 'flex-start'
};

const arrowIconSx: SxProps = {
  fill: 'white'
};

export const AccountsDropDown: React.FC = () => {
  const { signOut } = useAuth();
  const isSignOutHidden = Boolean(useFlag(Flags.HIDE_SIGN_OUT_CONTROL));
  const user = useUser();
  const organizations = useRecoilValue(organizationGroupOrganizationsAtom);
  const organization = useOrganization();
  const { changeOrganization } = useUserFetcher();
  const {
    palette: { border }
  } = useTheme();

  const organizationsOptions = useMemo<Option[]>(() => {
    if (organizations === null || !organizations.length) return [];

    return organizations.map<Option>((org) => ({
      label: org.name,
      value: org.id
    }));
  }, [organizations]);

  const selectedOption = useMemo<string>(() => {
    if (!organizationsOptions.length) return '';

    return organization.id;
  }, [organization, organizationsOptions]);

  const handleChange = (value: string) => {
    const chosenOrganization = organizations?.find((org) => org.id === value);
    if (!chosenOrganization) return;
    changeOrganization(user.id, chosenOrganization, user.organizationGroupRole);
  };

  const handleAdminButtonPressed = () => {
    window.open(Paths.ADMINISTRATOR, '_blank');
  };

  return (
    <Select
      sx={selectStyling}
      value={selectedOption}
      renderValue={() => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              fontSize: '16px',
              fontFamily: 'Instrument Sans, Arial, sans-serif'
            }}
          >
            {organization?.name}
          </Typography>
          {organization?.isSandbox && (
            <Box
              component="span"
              sx={{
                border: '1px solid white',
                backgroundColor: 'white',
                color: 'text.primary',
                borderRadius: '16px',
                px: 1,
                py: 0.25,
                fontSize: '12px',
                fontWeight: 'bold',
                lineHeight: 1.5
              }}
            >
              Sandbox
            </Box>
          )}
        </Box>
      )}
      IconComponent={(props) => (
        <KeyboardArrowDown sx={arrowIconSx} {...props} />
      )}
      onChange={(e) => {
        if (!e.target.value) return;
        handleChange(e.target.value as string);
      }}
    >
      {organizationsOptions.map((option) => {
        return (
          <MenuItem
            sx={menuItemStyling}
            key={option.value}
            value={option.value}
          >
            {organization?.id === option.value && (
              <CheckIcon color="primary" sx={{ mr: 1 }} />
            )}
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        );
      })}
      {!isSignOutHidden && (
        <MenuItem onClick={() => signOut()}>
          <Box sx={{ mb: 1 }}>
            <Button variant="tertiary" fullWidth sx={buttonStyling}>
              <Typography variant="body2">
                Sign out of all your accounts
              </Typography>
            </Button>
          </Box>
        </MenuItem>
      )}

      {user?.organizationGroupRole ===
        OrganizationGroupRole.OrganizationGroupAdmin && (
        <MenuItem
          sx={{
            ...administratorMenuItem,
            borderTop: border.small.grey.dark
          }}
          onClick={handleAdminButtonPressed}
        >
          <Box sx={{ py: 1 }}>
            <Button variant="tertiary" fullWidth sx={buttonStyling}>
              <OpenInNew color="primary" />
              <Typography variant="body2">Administrator</Typography>
            </Button>
          </Box>
        </MenuItem>
      )}
    </Select>
  );
};
